<template>
  <div class="tcla-priority">
    <!-- Top urgent list -->
    <div class="tcla-panel">
      <h3 class="tcla-panel__title">🔥 فوری‌ترین‌ها (۱۰ مورد اول)</h3>
      <p class="tcla-panel__hint">بر اساس امتیاز سئو + طول کلمه‌کلیدی (کلمه‌کلیدی دقیق‌تر = برد سریع‌تر)</p>
      <div v-if="topUrgent.length === 0" class="tcla-empty">همه‌چیز خوبه 🎉</div>
      <div v-for="(page, idx) in topUrgent" :key="page.url" class="tcla-urgent-card">
        <div class="tcla-urgent-card__head">
          <span class="tcla-urgent-row__rank">{{ idx + 1 }}</span>
          <span class="tcla-urgent-row__title">{{ page.title }}</span>
          <span class="tcla-score-pill" :class="scoreClass(page.seoScore)">{{ page.seoScore }}</span>
          <button class="tcla-btn" @click="$emit('edit-blog', page.source)">ویرایش</button>
        </div>
        <div v-if="page.actions && page.actions.length" class="tcla-urgent-card__actions">
          <div v-for="(action, ai) in page.actions" :key="ai" class="tcla-action-line">
            {{ action.icon }} {{ action.text }}
          </div>
        </div>
      </div>
    </div>

    <!-- Grouped issues -->
    <div class="tcla-panel">
      <h3 class="tcla-panel__title">📋 مشکلات به تفکیک نوع (برای کار دسته‌جمعی)</h3>
      <p class="tcla-panel__hint">هر گروه رو باز کن و همه‌ی مقالات هم‌مشکل رو یک‌جا ببین</p>

      <details v-for="group in groups" :key="group.key" class="tcla-group" :open="group.items.length > 0 && group.items.length <= 5">
        <summary class="tcla-group__summary">
          <span class="tcla-group__icon">{{ group.icon }}</span>
          <span class="tcla-group__title">{{ group.title }}</span>
          <span class="tcla-group__count" :class="{ 'tcla-group__count--zero': group.items.length === 0 }">{{ group.items.length }}</span>
        </summary>

        <div v-if="group.items.length === 0" class="tcla-empty tcla-empty--small">موردی نیست 🎉</div>
        <div v-else class="tcla-group__list">
          <div v-for="(item, i) in visibleItems(group)" :key="i" class="tcla-item-row">
            <div class="tcla-item-row__main">
              <strong>{{ item.title }}</strong>
              <span v-if="item.detail" class="tcla-item-row__detail">{{ item.detail }}</span>
            </div>
            <div class="tcla-item-row__actions">
              <button v-if="item.source !== undefined" class="tcla-btn" @click="$emit('edit-blog', item.source)">
                {{ item.source2 !== undefined ? 'ویرایش A' : 'ویرایش' }}
              </button>
              <button v-if="item.source2 !== undefined" class="tcla-btn" @click="$emit('edit-blog', item.source2)">ویرایش B</button>
            </div>
          </div>
          <button
              v-if="group.items.length > VISIBLE_LIMIT && !expanded[group.key]"
              class="tcla-btn tcla-btn--link"
              @click="expandGroup(group.key)"
          >
            نمایش همه‌ی {{ group.items.length }} مورد...
          </button>
        </div>
      </details>

      <!-- Over-optimized anchors: grouped by target page, each source shown
           as a compact chip (title + anchor text used + inline edit icon) -->
      <details class="tcla-group" :open="overOptimizedAnchorGroups.length > 0 && overOptimizedAnchorGroups.length <= 5">
        <summary class="tcla-group__summary">
          <span class="tcla-group__icon">⚓</span>
          <span class="tcla-group__title">انکرتکست بیش‌بهینه‌شده</span>
          <span class="tcla-group__count" :class="{ 'tcla-group__count--zero': overOptimizedAnchorGroups.length === 0 }">{{ overOptimizedAnchorGroups.length }}</span>
        </summary>
        <div v-if="overOptimizedAnchorGroups.length === 0" class="tcla-empty tcla-empty--small">موردی نیست 🎉</div>
        <div v-else class="tcla-group__list">
          <div v-for="(g, gi) in overOptimizedAnchorGroups" :key="gi" class="tcla-anchor-target-block">
            <div class="tcla-anchor-target-block__title">🎯 {{ g.targetTitle }}</div>
            <div class="tcla-chip-list">
              <span v-for="(chip, ci) in g.chips" :key="ci" class="tcla-anchor-chip">
                {{ chip.title }}
                <span class="tcla-anchor-chip__anchor">«{{ chip.anchorText }}»</span>
                <button
                    class="tcla-chip-edit-btn"
                    title="ویرایش این مقاله"
                    @click="$emit('edit-blog', chip.source)"
                >✎</button>
              </span>
            </div>
          </div>
        </div>
      </details>
    </div>
  </div>
</template>

<script>
const VISIBLE_LIMIT = 20;

export default {
  name: 'TCLAPriorityQueue',
  props: {
    contentPlan: { type: Array, default: () => [] },
    articleReports: { type: Array, default: () => [] },
    missingLinks: { type: Array, default: () => [] },
    irrelevantLinks: { type: Array, default: () => [] },
    similarArticlePairs: { type: Array, default: () => [] },
    orphans: { type: Array, default: () => [] },
    deadEnds: { type: Array, default: () => [] },
    anchorAnalysis: { type: Array, default: () => [] },
    productLinkAnalysis: {
      type: Object,
      default: () => ({ orphanProducts: [], articlesWithNoProductLinks: [], suggestions: [] })
    },
    graph: { type: Object, default: () => ({ nodes: {} }) }
  },
  data() {
    return { VISIBLE_LIMIT, expanded: {} };
  },
  computed: {
    topUrgent() {
      return this.contentPlan.slice(0, 10);
    },
    overOptimizedAnchorGroups() {
      const groups = [];
      this.anchorAnalysis.filter(a => a.overOptimized).forEach(a => {
        const targetNode = this.graph.nodes[a.url];
        if (!targetNode) return;
        const chips = targetNode.incomingLinks.map(link => {
          const srcNode = this.graph.nodes[link.url];
          return {
            title: srcNode ? srcNode.title : link.url,
            source: srcNode ? srcNode.source : undefined,
            anchorText: link.anchorText
          };
        });
        groups.push({ targetTitle: a.title, targetUrl: a.url, chips });
      });
      return groups;
    },
    groups() {
      const getSource = (url) => (this.graph.nodes[url] ? this.graph.nodes[url].source : undefined);

      return [
        {
          key: 'thin', icon: '📝', title: 'محتوای کم‌عمق (زیر ۳۰۰ کلمه)',
          items: this.articleReports
              .filter(a => a.contentDiagnostics.wordCount < 300)
              .map(a => ({ title: a.title, source: a.source, detail: `${a.contentDiagnostics.wordCount} کلمه` }))
        },
        {
          key: 'noImages', icon: '🖼️', title: 'بدون عکس',
          items: this.articleReports
              .filter(a => a.contentDiagnostics.imageCount === 0)
              .map(a => ({ title: a.title, source: a.source }))
        },
        {
          key: 'noAlt', icon: '🏷️', title: 'عکس بدون alt text',
          items: this.articleReports
              .filter(a => a.contentDiagnostics.imagesWithoutAlt > 0)
              .map(a => ({ title: a.title, source: a.source, detail: `${a.contentDiagnostics.imagesWithoutAlt} عکس بدون alt` }))
        },
        {
          key: 'fewHeadings', icon: '🔤', title: 'تیتر ناکافی (کمتر از ۳)',
          items: this.articleReports
              .filter(a => a.contentDiagnostics.headingCount < 3)
              .map(a => ({ title: a.title, source: a.source, detail: `${a.contentDiagnostics.headingCount} تیتر` }))
        },
        {
          key: 'missingLinks', icon: '🔗', title: 'لینک داخلی مفقود (بیشترین شباهت اول)',
          items: [...this.missingLinks]
              .sort((a, b) => (b.similarity || 0) - (a.similarity || 0))
              .map(m => ({
                title: m.sourceTitle, source: getSource(m.sourceUrl),
                detail: `→ ${m.targetTitle}` +
                    (m.similarity !== null && m.similarity !== undefined ? ` · شباهت ${m.similarity}٪` : '') +
                    ` · ${m.reason}`
              }))
        },
        {
          key: 'irrelevant', icon: '🚫', title: 'لینک بی‌ربط (باید حذف/اصلاح شود)',
          items: this.irrelevantLinks.map(l => ({
            title: l.sourceTitle, source: getSource(l.sourceUrl),
            detail: `→ ${l.targetTitle} · ${l.reason}`
          }))
        },
        {
          key: 'similarArticles', icon: '🔀', title: 'مقالات با شباهت موضوعی بالا (کاندید ادغام)',
          items: this.similarArticlePairs.map(p => ({
            title: `${p.titleA} ↔ ${p.titleB}`,
            source: p.sourceA,
            source2: p.sourceB,
            detail: p.reason
          }))
        },
        {
          key: 'orphans', icon: '🏝️', title: 'صفحات یتیم (بدون لینک ورودی)',
          items: this.orphans.map(o => ({ title: o.title, source: o.source }))
        },
        {
          key: 'deadEnds', icon: '🚧', title: 'صفحات بن‌بست (بدون لینک خروجی)',
          items: this.deadEnds.map(d => ({ title: d.title, source: d.source }))
        },
        {
          key: 'orphanProducts', icon: '🛍️', title: 'محصولات بدون لینک از مقاله',
          items: (this.productLinkAnalysis.orphanProducts || []).map(p => ({ title: p.title, source: undefined }))
        },
        {
          key: 'noProductLink', icon: '📦', title: 'مقالات بدون لینک به محصول',
          items: (this.productLinkAnalysis.articlesWithNoProductLinks || []).map(a => ({ title: a.title, source: a.source }))
        },
        {
          key: 'productSuggestions', icon: '💡', title: 'پیشنهاد لینک مقاله ← محصول',
          items: (this.productLinkAnalysis.suggestions || []).map(s => ({
            title: s.articleTitle, source: s.articleSource,
            detail: `→ ${s.productTitle} · ${s.reason}`
          }))
        }
      ];
    }
  },
  methods: {
    visibleItems(group) {
      if (this.expanded[group.key]) return group.items;
      return group.items.slice(0, VISIBLE_LIMIT);
    },
    expandGroup(key) {
      this.expanded = { ...this.expanded, [key]: true };
    },
    scoreClass(score) {
      if (score >= 70) return 'tcla-score--good';
      if (score >= 40) return 'tcla-score--mid';
      return 'tcla-score--bad';
    }
  }
};
</script>

<style scoped>
.tcla-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 20px;
  margin-bottom: 16px;
}
.tcla-panel__title { font-size: 0.95rem; font-weight: 700; margin: 0 0 6px 0; }
.tcla-panel__hint { font-size: 0.78rem; color: #9ca3af; margin: 0 0 14px 0; }
.tcla-empty { text-align: center; padding: 16px; color: #9ca3af; font-size: 0.85rem; }
.tcla-empty--small { padding: 10px; font-size: 0.8rem; }

.tcla-urgent-card {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: #f9fafb;
}
.tcla-urgent-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.tcla-urgent-card__actions {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e5e7eb;
}
.tcla-action-line {
  font-size: 0.78rem;
  color: #4b5563;
  padding: 3px 0;
  line-height: 1.5;
}

.tcla-urgent-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 4px;
  border-bottom: 1px solid #f3f4f6;
}
.tcla-urgent-row__rank {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #3b82f6;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tcla-urgent-row__title { flex: 1; font-size: 0.85rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.tcla-group {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  margin-bottom: 10px;
  overflow: hidden;
}
.tcla-group__summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  background: #f9fafb;
  list-style: none;
  user-select: none;
}
.tcla-group__summary::-webkit-details-marker { display: none; }
.tcla-group__icon { font-size: 1rem; }
.tcla-group__title { flex: 1; font-size: 0.85rem; font-weight: 600; color: #374151; }
.tcla-group__count {
  background: #fee2e2;
  color: #dc2626;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 100px;
}
.tcla-group__count--zero { background: #d1fae5; color: #16a34a; }

.tcla-group__list { padding: 8px 14px 14px; }

.tcla-anchor-target-block {
  padding: 10px 0;
  border-bottom: 1px dashed #f3f4f6;
}
.tcla-anchor-target-block:last-child { border-bottom: none; }
.tcla-anchor-target-block__title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}
.tcla-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tcla-anchor-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 100px;
  padding: 4px 6px 4px 10px;
  font-size: 0.76rem;
  color: #4b5563;
}
.tcla-anchor-chip__anchor { color: #9ca3af; }
.tcla-chip-edit-btn {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #3b82f6;
  font-size: 0.68rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.tcla-chip-edit-btn:hover { background: #3b82f6; color: #fff; border-color: #3b82f6; }

.tcla-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #f3f4f6;
  font-size: 0.82rem;
}
.tcla-item-row__main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.tcla-item-row__detail { font-size: 0.72rem; color: #9ca3af; }
.tcla-item-row__actions { display: flex; gap: 6px; flex-shrink: 0; }

.tcla-score-pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 100px;
  font-weight: 700;
  font-size: 0.72rem;
  flex-shrink: 0;
}
.tcla-score--good { background: #d1fae5; color: #16a34a; }
.tcla-score--mid { background: #fef3c7; color: #d97706; }
.tcla-score--bad { background: #fee2e2; color: #ef4444; }

.tcla-btn {
  padding: 5px 12px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.76rem;
  cursor: pointer;
  flex-shrink: 0;
}
.tcla-btn:hover { background: #f3f4f6; }
.tcla-btn--link { background: transparent; border: none; color: #3b82f6; margin-top: 6px; }
.tcla-btn--link:hover { text-decoration: underline; background: transparent; }
</style>